import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { DocumentHelperService } from "src/app/services/document-helper.service";
import { FreyaHelperService } from "src/app/services/freya-helper.service";

import { EditArtifactsGQL, RefreshDocumentGQL, SendDocumentGQL, SendDocumentMutationVariables, ViewArtifactsGQL } from "../../../generated/graphql.generated";
import { isAegisArtifact } from "../../artifacts/artifact.util";
import { DetailsHelperService } from "../../services/details-helper.service";
import { FreyaMutateService } from "../../services/freya-mutate.service";
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { jobToolFeature, selectArtifactsDictionary, selectJobId } from "../job-tool.reducer";


import { JobEstimateActions } from "./job-estimate.actions";
import { selectActionsMenuArtifact } from "./job-estimate.component";


export const sendDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  sendDocumentGQL = inject(SendDocumentGQL),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.sendDocumentButtonClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
        ]),
        map(([ { artifactId }, jobId ]): {
          vars: SendDocumentMutationVariables;
          jobId: string;
          artifactId: string;
        } => ({
          jobId,
          artifactId,
          vars: {
            artifactId,
            recipientRole: 'customer',
          },
        })),
        switchMap(({ vars, jobId, artifactId }) => sendDocumentGQL.mutate(vars).pipe(
          map(() => JobEstimateActions.sendDocumentSuccess({ jobId, artifactId })),
        )),
        catchError((error) => of(JobEstimateActions.sendDocumentError({ error, artifactId: action.artifactId }))),
      )),
    );
  }, { functional: true, dispatch: true });

export const openDocumentInDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobEstimateActions.documentClicked,
        JobEstimateActions.viewDocumentMenuItemClicked,
      ),
      concatLatestFrom((action) => store.select(createSelector(
        jobToolFeature.selectActionsMenuArtifactId,
        selectArtifactsDictionary,
        (actionsMenuArtifactId, artifacts) => {

          let artifactId = actionsMenuArtifactId;

          if (action.type === JobEstimateActions.documentClicked.type) {
            artifactId = action.artifactId;
          }

          return artifacts[artifactId];
        }
      ))),
      tap(([ _action, artifact ]) => freyaHelper.openInDialog(artifact)),
    );
  }, { functional: true, dispatch: false });

export const downloadDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.downloadDocumentMenuItemClicked),
      concatLatestFrom(() => store.select(selectActionsMenuArtifact)),
      tap(([ _action, artifact ]) => freyaHelper.downloadFile(artifact.signedUrl || artifact.url, artifact.name)),
    );
  }, { functional: true, dispatch: false });

export const openDeleteArtifactDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  mutateService = inject(FreyaMutateService),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.deleteDocumentMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectActionsMenuArtifact),
        store.select(selectJobId),
      ]),
      tap(([ _action, artifact, jobId ]) => mutateService.openDeleteObject({
        objectId: artifact.id,
        objectName: artifact.name,
        objectType: 'artifact',
        afterDelete: () => store.dispatch(JobEstimateActions.deleteDocumentSuccess({
          artifactId: artifact.id,
          jobId,
        })),
      })),
    );
  }, { functional: true, dispatch: false });

export const openDocumentAsRole = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobEstimateActions.viewAsCustomerMenuItemClicked,
        JobEstimateActions.viewAsEmployeeMenuItemClicked,
      ),
      map((action) => action.type === JobEstimateActions.viewAsEmployeeMenuItemClicked.type),
      map((viewAsEmployee) => viewAsEmployee ? 'employee' : 'customer'),
      concatLatestFrom(() => store.select(selectActionsMenuArtifact)),
      filter(([ _role, artifact ]) => artifact != null),
      // Aegis documents open the tokenized sign page; PandaDoc documents open
      // the PandaDoc embed. Both land in the same in-app dialog.
      tap(([ role, artifact ]) => isAegisArtifact(artifact)
        ? documentHelper.openAegisSignLink(artifact, role)
        : documentHelper.openInPandadoc(artifact, role)),
    );
  }, { functional: true, dispatch: false });

export const printDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.printMenuItemClicked ),
      concatLatestFrom(() => store.select(selectActionsMenuArtifact)),
      tap(([ _role, artifact ]) => documentHelper.printDocument(artifact)),
    );
  }, { functional: true, dispatch: false });

export const openDocumentInRightPanel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.openInRightPanelMenuItemClicked),
      concatLatestFrom(() => store.select(jobToolFeature.selectActionsMenuArtifactId)),
      tap(([ _action, artifactId ]) => detailsHelper.detailsItem.next({ type: 'artifact', item: { id: artifactId } })),
    );
  }, { functional: true, dispatch: false });

export const refreshDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  refreshDocumentGQL = inject(RefreshDocumentGQL),
  viewArtifactsGQL = inject(ViewArtifactsGQL),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(JobEstimateActions.refreshMenuItemClicked),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectActionsMenuArtifactId),
        store.select(selectJobId),
      ]),
      exhaustMap(([ _action, artifactId, jobId ]) => refreshDocumentGQL.mutate({ artifactId }).pipe(
        mergeMap(() => viewArtifactsGQL.fetch({
          filter: { ids: [ artifactId ] },
          getPrivate: true,
        }).pipe(
          map((res) => res.data.artifacts.artifacts[0]),
          tap((artifact) => freyaHelper.openInDialog(artifact)),
          map((artifact) => JobEstimateActions.refreshDocumentSuccess({
            artifact,
            jobId,
          })),
        )),
        catchError((error) => of(JobEstimateActions.refreshDocumentError({ error }))),
      )),
    );
  }, { functional: true, dispatch: true });

export const updateBolAttachment = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  editArtifactsGQL = inject(EditArtifactsGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(
        JobEstimateActions.attachToBolMenuItemClicked,
        JobEstimateActions.detachFromBolMenuItemClicked
      ),
      concatLatestFrom(() => store.select(selectActionsMenuArtifact)),
      mergeMap(([ action, artifact ]) => {
        const isAttach = action.type === JobEstimateActions.attachToBolMenuItemClicked.type;
        let attributes = [...(artifact.attributes || [])];
        
        if (isAttach) {
            if (!attributes.includes('include-in-bol')) {
                attributes.push('include-in-bol');
            }
        } else {
            attributes = attributes.filter(a => a !== 'include-in-bol');
        }

        return editArtifactsGQL.mutate({
            artifactEdits: [{
                artifactId: artifact.id,
                edit: {
                    attributes
                }
            }]
        }).pipe(
          tap(() => notify.success(isAttach ? 'Document attached to BOL' : 'Document detached from BOL')),
          catchError((_error) => {
            notify.error('Failed to update document');
            return of(null);
          })
        );

      })
    );
  }, { functional: true, dispatch: false });
